import React, { useMemo, useState } from "react";
import { Box, Button, ButtonGroup, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, Paper, SvgIcon, TextField, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import * as d3 from "d3";
import LayoutMapping, { useLayoutMappingMetrics } from "../components/LayoutMapping";
import WorkspaceSidebar from "../components/WorkspaceSidebar";
import { ARAGON_COLORS, ARAGON_FONTS } from "../theme/AragonTheme";
import { readAdvisorMeetings, updateAdvisorMeetingStatus } from "./advisorWorkspaceData";

function SearchIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <circle cx="11" cy="11" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="m16 16 4 4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </SvgIcon>
  );
}

function ChevronIcon({ direction = "left", ...props }) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d={direction === "left" ? "m14.5 6-6 6 6 6" : "m9.5 6 6 6-6 6"} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </SvgIcon>
  );
}

function useAdvisorCalendarMetrics() {
  const shared = useLayoutMappingMetrics();
  return useMemo(() => {
    const width = shared.viewport.width;
    const height = shared.viewport.height;
    return {
      ...shared,
      contentWidth: Math.round(d3.scaleLinear().domain([1024, 1920]).range([780, 1240]).clamp(true)(width)),
      contentTop: Math.round(d3.scaleLinear().domain([620, 1080]).range([18, 28]).clamp(true)(height)),
      toolbarHeight: Math.round(d3.scaleLinear().domain([768, 1920]).range([34, 40]).clamp(true)(width)),
      monthCellHeight: Math.round(d3.scaleLinear().domain([620, 1080]).range([78, 112]).clamp(true)(height)),
      hourHeight: Math.round(d3.scaleLinear().domain([620, 1080]).range([50, 64]).clamp(true)(height)),
      timeColumn: Math.round(d3.scaleLinear().domain([768, 1920]).range([62, 78]).clamp(true)(width)),
      eventInset: Math.round(d3.scaleLinear().domain([768, 1920]).range([4, 7]).clamp(true)(width)),
    };
  }, [shared]);
}

function dateKey(date) {
  return d3.timeFormat("%Y-%m-%d")(date);
}

function parseMeetingDate(meeting) {
  const [year, month, day] = meeting.date.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function minutesFromTime(value) {
  const [hour, minute] = value.split(":").map(Number);
  return hour * 60 + minute;
}

function formatLongDate(date) {
  return new Intl.DateTimeFormat("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" }).format(date);
}

function formatMonth(date) {
  return new Intl.DateTimeFormat("en-GB", { month: "long", year: "numeric" }).format(date);
}

function statusLabel(status) {
  if (status === "accepted") return "Accepted";
  if (status === "declined") return "Declined";
  return "Pending";
}

function MeetingCard({ meeting, onClick, compact = false, sx = {} }) {
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => onClick(meeting)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") onClick(meeting);
      }}
      sx={{
        p: compact ? "5px 6px" : "7px 8px",
        borderRadius: "6px",
        bgcolor: alpha(ARAGON_COLORS.navy, meeting.status === "pending" ? 0.055 : 0.035),
        border: `1px solid ${alpha(ARAGON_COLORS.navy, 0.10)}`,
        cursor: "pointer",
        overflow: "hidden",
        transition: "background-color 120ms ease, border-color 120ms ease",
        "&:hover": { bgcolor: alpha(ARAGON_COLORS.navy, 0.075), borderColor: alpha(ARAGON_COLORS.navy, 0.18) },
        ...sx,
      }}
    >
      <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: compact ? "8px" : "9px", fontWeight: 700, lineHeight: 1.25, color: ARAGON_COLORS.navy, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{meeting.partyName}</Typography>
      <Typography sx={{ mt: "2px", fontFamily: ARAGON_FONTS.secondary, fontSize: compact ? "7px" : "8px", lineHeight: 1.2, color: ARAGON_COLORS.steel, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{meeting.start}–{meeting.end} · {statusLabel(meeting.status)}</Typography>
    </Box>
  );
}

function MonthView({ currentDate, meetings, metrics, onMeeting }) {
  const monthStart = d3.timeMonth.floor(currentDate);
  const gridStart = d3.timeSunday.floor(monthStart);
  const days = d3.timeDay.range(gridStart, d3.timeDay.offset(gridStart, 42));
  const weekday = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const byDay = d3.group(meetings, (meeting) => meeting.date);
  return (
    <Paper elevation={0} sx={{ border: `1px solid ${alpha(ARAGON_COLORS.navy, 0.10)}`, borderRadius: `${metrics.cardRadius}px`, bgcolor: ARAGON_COLORS.sellerSidebar, overflow: "hidden" }}>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", borderBottom: `1px solid ${alpha(ARAGON_COLORS.navy, 0.09)}` }}>
        {weekday.map((day, index) => <Box key={day} sx={{ py: "9px", textAlign: "center", borderLeft: index === 0 ? "none" : `1px solid ${alpha(ARAGON_COLORS.navy, 0.08)}` }}><Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(8, metrics.bodySize - 3)}px`, fontWeight: 700, color: ARAGON_COLORS.navy }}>{day}</Typography></Box>)}
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
        {days.map((day, index) => {
          const key = dateKey(day);
          const inMonth = day.getMonth() === currentDate.getMonth();
          const dayMeetings = byDay.get(key) || [];
          const today = dateKey(day) === dateKey(new Date());
          return (
            <Box key={key} sx={{ minHeight: `${metrics.monthCellHeight}px`, p: "8px", borderLeft: index % 7 === 0 ? "none" : `1px solid ${alpha(ARAGON_COLORS.navy, 0.08)}`, borderTop: index < 7 ? "none" : `1px solid ${alpha(ARAGON_COLORS.navy, 0.08)}`, bgcolor: inMonth ? ARAGON_COLORS.sellerSidebar : alpha(ARAGON_COLORS.sellerCanvas, 0.52), boxSizing: "border-box", overflow: "hidden" }}>
              <Box sx={{ width: "22px", height: "22px", borderRadius: "50%", display: "grid", placeItems: "center", bgcolor: today ? ARAGON_COLORS.navy : alpha(ARAGON_COLORS.sellerSidebar, 0) }}><Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: "9px", color: today ? ARAGON_COLORS.sellerSidebar : inMonth ? ARAGON_COLORS.navy : ARAGON_COLORS.steel }}>{day.getDate()}</Typography></Box>
              <Box sx={{ mt: "5px", display: "flex", flexDirection: "column", gap: "4px" }}>
                {dayMeetings.slice(0, 2).map((meeting) => <MeetingCard key={meeting.id} meeting={meeting} onClick={onMeeting} compact />)}
                {dayMeetings.length > 2 && <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: "7px", color: ARAGON_COLORS.steel }}>+{dayMeetings.length - 2} more</Typography>}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}

function WeekView({ currentDate, meetings, metrics, onMeeting }) {
  const start = d3.timeMonday.floor(currentDate);
  const days = d3.timeDay.range(start, d3.timeDay.offset(start, 7));
  const hours = d3.range(24);
  const totalHeight = metrics.hourHeight * 24;
  const y = d3.scaleLinear().domain([0, 1440]).range([0, totalHeight]);
  return (
    <Paper elevation={0} sx={{ border: `1px solid ${alpha(ARAGON_COLORS.navy, 0.10)}`, borderRadius: `${metrics.cardRadius}px`, bgcolor: ARAGON_COLORS.sellerSidebar, overflow: "auto", maxHeight: `calc(100dvh - ${metrics.contentTop + 205}px)` }}>
      <Box sx={{ minWidth: 760 }}>
        <Box sx={{ position: "sticky", top: 0, zIndex: 4, display: "grid", gridTemplateColumns: `${metrics.timeColumn}px repeat(7, minmax(0, 1fr))`, bgcolor: ARAGON_COLORS.sellerSidebar, borderBottom: `1px solid ${alpha(ARAGON_COLORS.navy, 0.09)}` }}>
          <Box sx={{ py: "10px", textAlign: "center" }}><Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: "8px", fontWeight: 700, color: ARAGON_COLORS.steel }}>Time</Typography></Box>
          {days.map((day) => <Box key={dateKey(day)} sx={{ py: "8px", textAlign: "center", borderLeft: `1px solid ${alpha(ARAGON_COLORS.navy, 0.08)}` }}><Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: "9px", fontWeight: 700, color: ARAGON_COLORS.navy }}>{d3.timeFormat("%a")(day)}</Typography><Typography sx={{ mt: "2px", fontFamily: ARAGON_FONTS.secondary, fontSize: "8px", color: ARAGON_COLORS.steel }}>{d3.timeFormat("%b %-d")(day)}</Typography></Box>)}
        </Box>
        <Box sx={{ position: "relative", height: `${totalHeight}px`, display: "grid", gridTemplateColumns: `${metrics.timeColumn}px repeat(7, minmax(0, 1fr))` }}>
          <Box sx={{ position: "relative", borderRight: `1px solid ${alpha(ARAGON_COLORS.navy, 0.08)}` }}>
            {hours.map((hour) => <Typography key={hour} sx={{ position: "absolute", top: `${y(hour * 60) + 8}px`, left: "10px", fontFamily: ARAGON_FONTS.secondary, fontSize: "8px", color: ARAGON_COLORS.steel }}>{String(hour).padStart(2, "0")}:00</Typography>)}
          </Box>
          {days.map((day, dayIndex) => (
            <Box key={dateKey(day)} sx={{ position: "relative", borderLeft: dayIndex === 0 ? "none" : `1px solid ${alpha(ARAGON_COLORS.navy, 0.08)}` }}>
              {hours.map((hour) => <Box key={hour} sx={{ position: "absolute", top: `${y(hour * 60)}px`, left: 0, right: 0, borderTop: `1px solid ${alpha(ARAGON_COLORS.navy, 0.07)}` }} />)}
              {meetings.filter((meeting) => meeting.date === dateKey(day)).map((meeting) => {
                const top = y(minutesFromTime(meeting.start));
                const height = Math.max(28, y(minutesFromTime(meeting.end)) - top);
                return <MeetingCard key={meeting.id} meeting={meeting} onClick={onMeeting} sx={{ position: "absolute", top: `${top + metrics.eventInset}px`, left: `${metrics.eventInset}px`, right: `${metrics.eventInset}px`, height: `${Math.max(24, height - metrics.eventInset * 2)}px`, boxSizing: "border-box" }} />;
              })}
            </Box>
          ))}
        </Box>
      </Box>
    </Paper>
  );
}

function DayView({ currentDate, meetings, metrics, onMeeting }) {
  const hours = d3.range(24);
  const totalHeight = metrics.hourHeight * 24;
  const y = d3.scaleLinear().domain([0, 1440]).range([0, totalHeight]);
  const dayMeetings = meetings.filter((meeting) => meeting.date === dateKey(currentDate));
  return (
    <Paper elevation={0} sx={{ border: `1px solid ${alpha(ARAGON_COLORS.navy, 0.10)}`, borderRadius: `${metrics.cardRadius}px`, bgcolor: ARAGON_COLORS.sellerSidebar, overflow: "auto", maxHeight: `calc(100dvh - ${metrics.contentTop + 205}px)` }}>
      <Box sx={{ position: "relative", minWidth: 520, height: `${totalHeight}px`, display: "grid", gridTemplateColumns: `${metrics.timeColumn}px minmax(0, 1fr)` }}>
        <Box sx={{ position: "relative", borderRight: `1px solid ${alpha(ARAGON_COLORS.navy, 0.08)}` }}>
          {hours.map((hour) => <Typography key={hour} sx={{ position: "absolute", top: `${y(hour * 60) + 8}px`, left: "12px", fontFamily: ARAGON_FONTS.secondary, fontSize: "8px", color: ARAGON_COLORS.steel }}>{String(hour).padStart(2, "0")}:00</Typography>)}
        </Box>
        <Box sx={{ position: "relative" }}>
          {hours.map((hour) => <Box key={hour} sx={{ position: "absolute", top: `${y(hour * 60)}px`, left: 0, right: 0, borderTop: `1px solid ${alpha(ARAGON_COLORS.navy, 0.07)}` }} />)}
          {dayMeetings.map((meeting) => {
            const top = y(minutesFromTime(meeting.start));
            const height = Math.max(34, y(minutesFromTime(meeting.end)) - top);
            return <MeetingCard key={meeting.id} meeting={meeting} onClick={onMeeting} sx={{ position: "absolute", top: `${top + metrics.eventInset}px`, left: `${metrics.eventInset + 4}px`, right: `${metrics.eventInset + 4}px`, height: `${Math.max(30, height - metrics.eventInset * 2)}px`, boxSizing: "border-box" }} />;
          })}
        </Box>
      </Box>
    </Paper>
  );
}

export default function AdvisorCalendar() {
  const metrics = useAdvisorCalendarMetrics();
  const [view, setView] = useState("month");
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [query, setQuery] = useState("");
  const [meetings, setMeetings] = useState(() => readAdvisorMeetings());
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const filteredMeetings = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    return meetings.filter((meeting) => !normalized || `${meeting.partyName} ${meeting.partyType} ${meeting.requestedBy} ${meeting.status}`.toLocaleLowerCase().includes(normalized));
  }, [meetings, query]);
  const title = view === "month" ? formatMonth(currentDate) : view === "week" ? `Week of ${d3.timeFormat("%b %-d")(d3.timeMonday.floor(currentDate))}` : formatLongDate(currentDate);
  const shift = (direction) => {
    const offset = direction === "next" ? 1 : -1;
    if (view === "month") setCurrentDate(d3.timeMonth.offset(currentDate, offset));
    else if (view === "week") setCurrentDate(d3.timeWeek.offset(currentDate, offset));
    else setCurrentDate(d3.timeDay.offset(currentDate, offset));
  };
  const setMeetingStatus = (status) => {
    if (!selectedMeeting) return;
    const next = updateAdvisorMeetingStatus(selectedMeeting.id, status);
    setMeetings(next);
    setSelectedMeeting({ ...selectedMeeting, status });
  };
  return (
    <LayoutMapping metrics={metrics} sidebar={<WorkspaceSidebar metrics={metrics} workspace="advisor" />}>
      <Box>
        <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${metrics.eyebrowSize}px`, fontWeight: 800, letterSpacing: "0.15em", textTransform: "uppercase", color: ARAGON_COLORS.burgundy }}>Advisor workspace</Typography>
        <Typography component="h1" sx={{ mt: "5px", fontFamily: ARAGON_FONTS.primary, fontSize: `${metrics.titleSize}px`, lineHeight: 1.04, color: ARAGON_COLORS.navy }}>Calendar</Typography>
        <Box sx={{ mt: `${metrics.cardGap + 8}px`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
            <Typography sx={{ mr: "4px", fontFamily: ARAGON_FONTS.primary, fontSize: `${metrics.sectionTitleSize + 2}px`, color: ARAGON_COLORS.navy, whiteSpace: "nowrap" }}>{title}</Typography>
            <Button onClick={() => shift("prev")} sx={{ minWidth: `${metrics.toolbarHeight}px`, width: `${metrics.toolbarHeight}px`, height: `${metrics.toolbarHeight}px`, p: 0, border: `1px solid ${alpha(ARAGON_COLORS.navy, 0.12)}`, color: ARAGON_COLORS.navy }}><ChevronIcon direction="left" sx={{ fontSize: 16 }} /></Button>
            <Button onClick={() => setCurrentDate(new Date())} sx={{ height: `${metrics.toolbarHeight}px`, px: "13px", border: `1px solid ${alpha(ARAGON_COLORS.navy, 0.12)}`, color: ARAGON_COLORS.navy, fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(9, metrics.bodySize - 2)}px` }}>Today</Button>
            <Button onClick={() => shift("next")} sx={{ minWidth: `${metrics.toolbarHeight}px`, width: `${metrics.toolbarHeight}px`, height: `${metrics.toolbarHeight}px`, p: 0, border: `1px solid ${alpha(ARAGON_COLORS.navy, 0.12)}`, color: ARAGON_COLORS.navy }}><ChevronIcon direction="right" sx={{ fontSize: 16 }} /></Button>
          </Box>
          <ButtonGroup variant="outlined" sx={{ height: `${metrics.toolbarHeight}px`, "& .MuiButton-root": { px: "14px", borderColor: alpha(ARAGON_COLORS.navy, 0.12), color: ARAGON_COLORS.navy, fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(9, metrics.bodySize - 2)}px` } }}>
            {["month", "week", "day"].map((item) => <Button key={item} onClick={() => setView(item)} sx={{ bgcolor: view === item ? alpha(ARAGON_COLORS.navy, 0.075) : alpha(ARAGON_COLORS.sellerSidebar, 0), fontWeight: view === item ? 700 : 500 }}>{item[0].toUpperCase() + item.slice(1)}</Button>)}
          </ButtonGroup>
        </Box>
        <TextField
          fullWidth
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search meetings..."
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 17, color: ARAGON_COLORS.steel }} /></InputAdornment> }}
          inputProps={{ style: { fontFamily: ARAGON_FONTS.secondary, fontSize: Math.max(10, metrics.bodySize - 1) } }}
          sx={{ mt: "12px", mb: "12px", "& .MuiOutlinedInput-root": { height: `${metrics.toolbarHeight}px`, borderRadius: `${metrics.cardRadius}px`, bgcolor: ARAGON_COLORS.sellerSidebar, "& fieldset": { borderColor: alpha(ARAGON_COLORS.navy, 0.10) }, "&:hover fieldset": { borderColor: alpha(ARAGON_COLORS.navy, 0.18) }, "&.Mui-focused fieldset": { borderColor: ARAGON_COLORS.navy, borderWidth: "1px" } } }}
        />
        {view === "month" && <MonthView currentDate={currentDate} meetings={filteredMeetings} metrics={metrics} onMeeting={setSelectedMeeting} />}
        {view === "week" && <WeekView currentDate={currentDate} meetings={filteredMeetings} metrics={metrics} onMeeting={setSelectedMeeting} />}
        {view === "day" && <DayView currentDate={currentDate} meetings={filteredMeetings} metrics={metrics} onMeeting={setSelectedMeeting} />}
      </Box>
      <Dialog open={Boolean(selectedMeeting)} onClose={() => setSelectedMeeting(null)} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: `${metrics.cardRadius}px`, bgcolor: ARAGON_COLORS.sellerSidebar, border: `1px solid ${alpha(ARAGON_COLORS.navy, 0.10)}` } }} BackdropProps={{ sx: { bgcolor: alpha(ARAGON_COLORS.navy, 0.28) } }}>
        <DialogTitle sx={{ pb: "5px", fontFamily: ARAGON_FONTS.primary, fontSize: `${metrics.sectionTitleSize + 3}px`, color: ARAGON_COLORS.navy }}>Meeting request</DialogTitle>
        {selectedMeeting && <DialogContent sx={{ pt: "8px !important" }}>
          <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${metrics.bodySize}px`, fontWeight: 700, color: ARAGON_COLORS.navy }}>{selectedMeeting.partyName}</Typography>
          <Typography sx={{ mt: "3px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(8, metrics.bodySize - 3)}px`, color: ARAGON_COLORS.steel }}>{selectedMeeting.partyType === "seller" ? "Seller" : "Buyer"}</Typography>
          <Box sx={{ mt: "16px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
            <Box><Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: "8px", textTransform: "uppercase", letterSpacing: "0.10em", color: ARAGON_COLORS.steel }}>Date</Typography><Typography sx={{ mt: "4px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(9, metrics.bodySize - 2)}px`, color: ARAGON_COLORS.navy }}>{new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" }).format(parseMeetingDate(selectedMeeting))}</Typography></Box>
            <Box><Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: "8px", textTransform: "uppercase", letterSpacing: "0.10em", color: ARAGON_COLORS.steel }}>Time</Typography><Typography sx={{ mt: "4px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(9, metrics.bodySize - 2)}px`, color: ARAGON_COLORS.navy }}>{selectedMeeting.start} – {selectedMeeting.end}</Typography></Box>
            <Box><Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: "8px", textTransform: "uppercase", letterSpacing: "0.10em", color: ARAGON_COLORS.steel }}>Requested by</Typography><Typography sx={{ mt: "4px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(9, metrics.bodySize - 2)}px`, color: ARAGON_COLORS.navy }}>{selectedMeeting.requestedBy}</Typography></Box>
            <Box><Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: "8px", textTransform: "uppercase", letterSpacing: "0.10em", color: ARAGON_COLORS.steel }}>Status</Typography><Typography sx={{ mt: "4px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(9, metrics.bodySize - 2)}px`, fontWeight: 700, color: ARAGON_COLORS.navy }}>{statusLabel(selectedMeeting.status)}</Typography></Box>
          </Box>
        </DialogContent>}
        <DialogActions sx={{ px: "24px", pb: "20px", gap: "8px" }}>
          <Button onClick={() => setMeetingStatus("declined")} sx={{ height: "36px", px: "16px", border: `1px solid ${alpha(ARAGON_COLORS.navy, 0.14)}`, color: ARAGON_COLORS.navy, fontFamily: ARAGON_FONTS.secondary }}>Decline</Button>
          <Button onClick={() => setMeetingStatus("accepted")} sx={{ height: "36px", px: "18px", bgcolor: ARAGON_COLORS.navy, color: ARAGON_COLORS.sellerSidebar, fontFamily: ARAGON_FONTS.secondary, "&:hover": { bgcolor: alpha(ARAGON_COLORS.navy, 0.90) } }}>Accept</Button>
        </DialogActions>
      </Dialog>
    </LayoutMapping>
  );
}
